import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { NxCommandBar } from './NxCommandBar';
import { NxMenuGrid } from './NxMenuGrid';
import { Gauge, FolderOpen, Menu, User, Settings } from 'lucide-react';
import { useActivityTracker } from '../../hooks/useActivityTracker';
import '../../styles/nx.css';

/**
 * Phase 3 v2 — THERMS-style shell.
 * The persistent side rail has been removed. Navigation now lives in two
 * surfaces:
 *   1. A mobile bottom-nav with 5 quick-action tiles (Home / Cases / Menu
 *      / Hiring / Settings).
 *   2. A fullscreen <NxMenuGrid /> popup that exposes every module as a
 *      navy-blue square tile (plus a red "Go Off-Duty" sign-out tile at
 *      the bottom). Triggered by the Menu button — works on every viewport.
 *
 * The Dallas-skyline + eagle hero is preserved on the Dashboard page; this
 * shell only owns the chrome around it.
 */
export function NxShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  useActivityTracker();

  const onSignOut = () => { logout(); navigate('/login'); };

  return (
    <div className="nx nx-app" data-testid="nx-shell" style={{ height: '100dvh', minHeight: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#0B0F19' }}>
      <NxCommandBar
        user={user}
        onToggleSidebar={() => setMenuOpen(true)}
      />

      <main
        className="nx-workspace nx-mobile-bottompad"
        style={{ flex: '1 1 auto', minHeight: 0, padding: '20px 22px 0', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', background: '#0B0F19' }}
        data-testid="nx-workspace"
      >
        <Outlet />
      </main>

      {/* THERMS-style mobile bottom nav. The MENU button opens the
          fullscreen module grid (the same grid the topbar uses). */}
      <nav className="nx-bottom-nav lg:hidden" data-testid="mobile-bottom-nav" aria-label="Quick navigation">
        <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'active' : '')} data-testid="bnav-dashboard">
          <Gauge style={{ width: 20, height: 20 }} />
          <span>HOME</span>
        </NavLink>
        <NavLink to="/cases" className={({ isActive }) => (isActive ? 'active' : '')} data-testid="bnav-cases">
          <FolderOpen style={{ width: 20, height: 20 }} />
          <span>CASES</span>
        </NavLink>
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2,
            color: '#94A3B8', background: 'transparent', border: 'none', padding: 0,
            fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '0.08em',
            cursor: 'pointer',
          }}
          data-testid="bnav-menu"
          aria-label="Open module menu"
        >
          <Menu style={{ width: 20, height: 20 }} />
          <span>MENU</span>
        </button>
        <NavLink to="/nexus-new-hire" className={({ isActive }) => (isActive ? 'active' : '')} data-testid="bnav-recruit">
          <User style={{ width: 20, height: 20 }} />
          <span>HIRING</span>
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : '')} data-testid="bnav-settings">
          <Settings style={{ width: 20, height: 20 }} />
          <span>SETTINGS</span>
        </NavLink>
      </nav>

      {menuOpen && (
        <NxMenuGrid
          user={user}
          onClose={() => setMenuOpen(false)}
          onSignOut={onSignOut}
        />
      )}
    </div>
  );
}

export default NxShell;
